import React, { useEffect, useReducer, useState } from "react";
import {
  FilledInput,
  Button,
  Box,
  Modal,
  SxProps,
  Theme,
  Typography,
  FormControl,
  useTheme,
  FormLabel,
  FormHelperText,
  Stack,
  InputAdornment,
  Tooltip,
  CircularProgress,
  Alert,
} from "@mui/material";
import { AddressTestingStyles } from "../common/styles/AddressTesting.styles";
import { useIsMobile } from "src/utils/hooks/useIsMobile";
import clsx from "clsx";
import CloseIcon from "@mui/icons-material/Close";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useVerifyAddressMutation } from "src/store/features/api/generated";
import AddressSuccessScreen from "./AddressSuccessScreen";
import NumberFormatCustom from "src/components/trade/NumberField";
import { RowDataType } from "src/pages/Addresses";
import { trackEvent } from "src/Amplitude";
import { useUserSlice } from "src/store/features/user.slice";

type Props = {
  open: boolean;
  handleClose: () => void;
  selectedRow: RowDataType | null;
  refetchAddress: () => void;
};

const mobileStyle: SxProps<Theme> = {
  position: "fixed",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  bgcolor: "background.paper",
  borderRadius: "10px",
};

const desktopStyle: SxProps<Theme> = {
  position: "absolute",
  top: "50%",
  left: "50%",
  height: "auto",
  transform: "translate(-50%, -50%)",
  width: 400,
  bgcolor: "background.paper",
  boxShadow: 24,
  borderRadius: "10px",
  display: "flex",
  flexDirection: "column",
};

const AddressAmountModal = ({ open, handleClose, selectedRow, refetchAddress }: Props) => {
  // ---------------Sytles------------------------
  const theme = useTheme();
  const classes = AddressTestingStyles();
  const isMobile = useIsMobile();
  const style = isMobile ? mobileStyle : desktopStyle;
  const [amount, setAmount] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  //-------------------HOOKS------------------
  const [verifySuccess, toggleVerifySuccess] = useReducer((isSuccess) => !isSuccess, false);
  //-----------------RTKQ---------------------
  const [verify, { isLoading }] = useVerifyAddressMutation();
  const { is2FAEnabled } = useUserSlice();
  //-----------------Cosnts-------------------
  const title = "Verify Address";
  const instructions = [
    "Check your wallet for the amount sent.",
    "Input the amount below.",
    'Click "Verify"',
  ] as const;

  const handleVerify = async () => {
    setErrorMessage(null); // Clear error message
    const parsedAmount = parseFloat(amount);
    if (isNaN(parsedAmount)) {
      setErrorMessage("Invalid amount. Please input a valid number.");
      return;
    }
    if (selectedRow) {
      const payload = {
        verifyAddressDto: {
          address: selectedRow.address,
          testAmount: parsedAmount,
          token: selectedRow.code,
        },
      };

      try {
        await verify(payload)
          .unwrap()
          .then((resp) => {
            trackEvent("Address_Testing_Verified_Amount_Success");
            toggleVerifySuccess();
            handleClose();
            setAmount("");
            refetchAddress();
          });
      } catch (error: any) {
        if (error) {
          if (error && error.status === 400) {
            // This is a backend error
            setErrorMessage("Verification failed. Please check the amount and try again.");
          } else {
            // This is a random error
            setErrorMessage("An error occurred. Please try again later.");
          }
        } else {
          // This is a totally unexpected error
          setErrorMessage("An unexpected error occurred. Please try again later.");
        }
      }
    }
  };
  // cleanup function when the modal is closed
  useEffect(() => {
    if (!open) {
      setErrorMessage(null);
      setAmount("");
    }
  }, [open]);

  const truncateAddress = (address: string) => {
    const beginning = address.substring(0, 6);
    const end = address.substring(address.length - 4);
    return `${beginning}...${end}`;
  };

  if (verifySuccess) {
    return (
      <AddressSuccessScreen
        open={verifySuccess}
        isMobile={isMobile}
        address={truncateAddress(selectedRow?.address ?? "Error")}
      />
    );
  }

  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={style}>
        <Box
          className={clsx(classes.titleContainer, {
            [classes.titleContainerDesktop]: !isMobile,
          })}>
          <CloseIcon onClick={handleClose} className={classes.closeIcon} />
          <Typography id="modal-title" variant="h6" component="h2">
            {title}
          </Typography>
          <div></div>
        </Box>
        <Box
          className={clsx(classes.mainContainerMobile, {
            [classes.mainContainerDesktop]: !isMobile,
          })}>
          <Typography sx={{ mb: 3, fontWeight: 700, fontSize: "14px" }}>
            {`We've sent a small amount of crypto to address `}
            {selectedRow ? (
              <Tooltip title={selectedRow.address} arrow>
                <b>{truncateAddress(selectedRow.address)}</b>
              </Tooltip>
            ) : (
              "Loading..."
            )}
            {` for verification.`}
          </Typography>

          <Stack direction="column" sx={{ width: "100%", marginBottom: "28px" }}>
            {instructions.map((instruction, index) => (
              <Typography variant="body1" key={index} sx={{ fontSize: "14px", textAlign: "left" }}>
                {index + 1}. {instruction}
              </Typography>
            ))}
          </Stack>

          <FormControl className={classes.formContainer}>
            <Box display="flex" alignItems="center">
              <FormLabel className={classes.formLabels} id="network-label">
                Amount
              </FormLabel>
              <Tooltip
                title="This verification process confirms you're the owner of the wallet, enhancing security and ensuring crypto is sent to the correct destination."
                placement="right"
                classes={{ tooltip: classes.tooltip }}
                sx={{ ml: "3px", fontSize: "15px", mb: "12px" }}>
                <InfoOutlinedIcon />
              </Tooltip>
            </Box>
            <FilledInput
              type="standard"
              id="crypto-amount-input"
              className={classes.amountInput}
              defaultValue={amount}
              onChange={({ target: { value } }) => setAmount(value)}
              disableUnderline
              sx={{
                flex: 2,
                border: 1,
                borderColor: "transparent",
                "& .MuiInputBase-input": {
                  padding: "8px",
                  fontSize: "14px",
                  fontWeight: 600,
                },
                "&.Mui-focused": {
                  border: 1,
                  borderColor: theme.palette.primary.main,
                },
              }}
              inputComponent={NumberFormatCustom as any}
              endAdornment={<InputAdornment position="end">Units</InputAdornment>}
            />
            {errorMessage && (
              <FormHelperText className={classes.formHelperText} error>
                <ErrorOutlineOutlinedIcon className={classes.errorIcon} />
                {errorMessage}
              </FormHelperText>
            )}
          </FormControl>
          {!is2FAEnabled && (
            <Alert sx={{ width: "100%", mb: 2 }} severity="error">
              Please enable two-factor authentication
            </Alert>
          )}
          <Stack direction="row" justifyContent="space-around" className={classes.btnContainer}>
            <Button
              disableRipple
              variant="contained"
              color="primary"
              fullWidth
              disabled={amount === "" || isLoading || !is2FAEnabled}
              onClick={handleVerify}
              sx={{ backgroundColor: theme.palette.secondary.main }}>
              {isLoading ? <CircularProgress size={20} /> : "Verify"}
            </Button>
          </Stack>
        </Box>
      </Box>
    </Modal>
  );
};

export default AddressAmountModal;
